import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { FiDownloadCloud } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import './AnalysisReportStyles.css';
import Api from '../../services/Api';
import { Helmet } from 'react-helmet';

const AnalysisReport = () => {
    const { idCall } = useParams();
    const accessKey = new URLSearchParams(location.search).get("access-key");
    const [analysisData, setAnalysisData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [passwordRequired, setPasswordRequired] = useState(true);
    const [password, setPassword] = useState(accessKey ? accessKey : '');
    const [passwordError, setPasswordError] = useState('');
    const [generatingPDF, setGeneratingPDF] = useState(false);
    const reportRef = useRef();

    // Carrega os dados da análise
    useEffect(() => {
        if (idCall) {
            loadAnalysisData();
        }
    }, [idCall]);

    const loadAnalysisData = async () => {
        try {
            setLoading(true);
            const response = await Api.get(`/analysis/${idCall}/report`);

            if (response?.analysis) {
                setAnalysisData(response);

                // Se tem access_key, requer senha
                if (response?.analysis?.access_key) {
                    setPasswordRequired(true);
                } else {
                    setPasswordRequired(false);
                }
            }

        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const validatePassword = async () => {
        try {
            const response = await Api.post(`/analysis/${idCall}/validate-password`, {
                password: password,
            });
            if (response?.valid) {
                setPasswordRequired(false);
                setPasswordError('');
            } else {
                setPasswordError(response.message || 'Senha incorreta');
            }
        } catch (error) {
            setPasswordError('Erro ao validar senha');
        }
    };

    // Função para aguardar o carregamento de todas as imagens
    const waitForImages = (element) => {
        return new Promise((resolve) => {
            const images = element.querySelectorAll('img');
            let loadedImages = 0;
            const totalImages = images.length;

            if (totalImages === 0) {
                resolve();
                return;
            }

            const imageLoaded = () => {
                loadedImages++;
                if (loadedImages === totalImages) {
                    resolve();
                }
            };

            images.forEach((img) => {
                if (img.complete) {
                    imageLoaded();
                } else {
                    img.onload = imageLoaded;
                    img.onerror = imageLoaded; // Conta como carregada mesmo se houver erro
                }
            });
        });
    };

    const generatePDF = async () => {
        try {
            setGeneratingPDF(true);

            const element = reportRef.current;
            if (!element) {
                throw new Error('Elemento do relatório não encontrado');
            }

            // Cria um clone para manipular o layout sem afetar a visualização atual
            const clone = element.cloneNode(true);

            // Define largura fixa para simular desktop e garantir qualidade (A3 ~297mm largura)
            // Usamos 1200px para garantir o layout de desktop
            const renderWidth = 1200;

            Object.assign(clone.style, {
                width: `${renderWidth}px`,
                maxWidth: 'none',
                height: 'auto',
                position: 'fixed',
                left: '-10000px',
                top: '0',
                margin: '0',
                padding: '20px',
                backgroundColor: '#ffffff',
                zIndex: '-1000'
            });

            // Ajusta containers internos para ocupar toda a largura
            const container = clone.querySelector('.container');
            if (container) {
                container.style.maxWidth = '100%';
                container.style.width = '100%';
            }

            document.body.appendChild(clone);

            try {
                // Converte todas as imagens para Base64 para garantir renderização correta
                // Isso resolve problemas de CORS e carregamento assíncrono no html2canvas
                const images = Array.from(clone.querySelectorAll('img'));

                await Promise.all(images.map(async (img) => {
                    try {
                        if (!img.src || img.src.startsWith('data:')) return;

                        // Busca a imagem
                        const response = await fetch(img.src, { mode: 'cors' });
                        const blob = await response.blob();

                        // Converte para Base64
                        const base64 = await new Promise((resolve, reject) => {
                            const reader = new FileReader();
                            reader.onloadend = () => resolve(reader.result);
                            reader.onerror = reject;
                            reader.readAsDataURL(blob);
                        });

                        img.src = base64;
                        img.srcset = ''; // Remove srcset para forçar o uso do src Base64
                        img.crossOrigin = 'anonymous';
                    } catch (err) {
                        console.warn('Erro ao processar imagem para PDF:', img.src, err);
                    }
                }));

                // Aguarda um momento para o browser renderizar as imagens atualizadas no clone
                await new Promise(resolve => setTimeout(resolve, 1000));

                // Gera o Canvas a partir do clone
                const canvas = await html2canvas(clone, {
                    scale: 2, // Scale 2 garante boa definição para impressão
                    useCORS: true,
                    allowTaint: true,
                    backgroundColor: '#ffffff',
                    width: renderWidth,
                    windowWidth: renderWidth, // Força media queries de desktop
                    logging: false
                });

                // Configurações do PDF (A3)
                const pdf = new jsPDF({
                    orientation: 'portrait',
                    unit: 'mm',
                    format: 'a3',
                    compress: true
                });

                // Dimensões A3 em mm
                const pageWidth = 297;
                const pageHeight = 420;
                const margin = 10;
                const contentWidth = pageWidth - (margin * 2);

                // Calcula a escala para caber na largura da página A3
                const scale = contentWidth / canvas.width;
                const pageContentHeightMm = pageHeight - (margin * 2);
                const pageContentHeightCanvasPx = pageContentHeightMm / scale; // Altura da página em pixels do canvas

                let currentCanvasY = 0;
                let page = 0;

                // Fatiar o canvas em páginas do PDF
                while (currentCanvasY < canvas.height) {
                    if (page > 0) pdf.addPage();

                    // Altura desta fatia (pode ser menor na última página)
                    const sliceHeightPx = Math.min(pageContentHeightCanvasPx, canvas.height - currentCanvasY);

                    // Cria canvas temporário para a fatia
                    const sliceCanvas = document.createElement('canvas');
                    sliceCanvas.width = canvas.width;
                    sliceCanvas.height = sliceHeightPx;

                    const ctx = sliceCanvas.getContext('2d');
                    ctx.drawImage(
                        canvas,
                        0, currentCanvasY, canvas.width, sliceHeightPx,
                        0, 0, sliceCanvas.width, sliceHeightPx
                    );

                    const sliceImgData = sliceCanvas.toDataURL('image/png', 1.0);
                    const sliceHeightMm = sliceHeightPx * scale;

                    pdf.addImage(
                        sliceImgData,
                        'PNG',
                        margin,
                        margin,
                        contentWidth,
                        sliceHeightMm
                    );

                    currentCanvasY += sliceHeightPx;
                    page++;
                }

                const fileName = `relatorio_iridologico_${idCall}.pdf`;
                pdf.save(fileName);

            } finally {
                // Limpa o clone do DOM
                if (document.body.contains(clone)) {
                    document.body.removeChild(clone);
                }
            }
        } catch (error) {
            console.error('Erro ao gerar PDF:', error);
            alert('Erro ao gerar PDF. Tente novamente mais tarde!');
        } finally {
            setGeneratingPDF(false);
        }
    };

    // Função para pré-carregar imagens críticas
    const preloadImages = () => {
        if (!analysisData?.analysis?.eye_groups) return;

        Object.values(analysisData.analysis.eye_groups).forEach(eyeData => {
            if (eyeData.annotated_image) {
                const img = new Image();
                img.crossOrigin = 'anonymous';
                img.src = eyeData.annotated_image;
            }
            eyeData.images?.forEach(imageData => {
                if (imageData.image_src) {
                    const img = new Image();
                    img.crossOrigin = 'anonymous';
                    img.src = imageData.image_src;
                }
            });
        });
    };

    // Pré-carrega imagens quando os dados são carregados
    useEffect(() => {
        if (analysisData && !passwordRequired) {
            preloadImages();
        }
    }, [analysisData, passwordRequired]);

    if (passwordRequired) {
        return (
            <div className="password-container">
                <div className="password-card">
                    <h2>Acesso Restrito</h2>
                    <p>Este relatório é confidencial. Digite a senha de 4 dígitos para acessar:</p>
                    <div className="password-form">
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Digite a senha (4 dígitos)"
                            maxLength="4"
                            pattern="[0-9]{4}"
                            onKeyPress={(e) => e.key === 'Enter' && validatePassword()}
                        />
                        <button onClick={validatePassword} disabled={password.length !== 4}>
                            Acessar
                        </button>
                    </div>
                    {passwordError && <p className="error-message">{passwordError}</p>}
                    <div className="password-hint">
                        <small>A senha foi enviada para o seu e-mail, junto ao link de acesso ao relatório.</small>
                    </div>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Carregando relatório...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="error-container">
                <h2>Erro</h2>
                <p>{error}</p>
                <button onClick={() => window.location.reload()} className="retry-button">
                    Tentar Novamente
                </button>
            </div>
        );
    }

    if (!analysisData || !analysisData.analysis) {
        return (
            <div className="error-container">
                <h2>Relatório não encontrado</h2>
                <p>O relatório solicitado não foi encontrado ou ainda não foi gerado.</p>
            </div>
        );
    }

    return (
        <div className="analysis-report-page">

            <Helmet>
                <title>Resultado Análise Preventiva | E-IRIS</title>
            </Helmet>

            <div
                ref={reportRef}
                className="report-container"
                dangerouslySetInnerHTML={{ __html: analysisData.analysis.report_html }}
            />

            <div className="specialist-card">
                <div className="specialist-content">
                    <h3>Fale com um especialista</h3>
                    <p>Receba orientações e tenha um acompanhamento individualizado diretamente no WhatsApp.</p>
                </div>
                <a
                    href={`https://wa.me/5555996231504?text=${encodeURIComponent(`Olá, gostaria de um acompanhamento individualizado, referente ao meu relatório de análise do e-ris.\n${window.location.href}`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="whatsapp-button"
                >
                    <FaWhatsapp size={24} />
                    Fale com um especialista
                </a>
            </div>

            <div className="report-actions">
                <button
                    onClick={generatePDF}
                    className="pdf-button"
                    disabled={generatingPDF}
                >
                    {generatingPDF ? (
                        <>
                            <span className="loading-spinner-small"></span>
                            Gerando PDF...
                        </>
                    ) : (
                        <>
                            <FiDownloadCloud /> Baixar PDF
                        </>
                    )}
                </button>
            </div>

            {/* Componente para pré-carregar imagens de forma invisível */}
            <div style={{ display: 'none' }}>
                {analysisData?.analysis?.eye_groups &&
                    Object.values(analysisData.analysis.eye_groups).map((eyeData, index) => (
                        <div key={index}>
                            {eyeData.annotated_image && (
                                <img
                                    src={eyeData.annotated_image}
                                    alt="preload"
                                    crossOrigin="anonymous"
                                />
                            )}
                            {eyeData.images?.map((imageData, imgIndex) => (
                                <img
                                    key={imgIndex}
                                    src={imageData.image_src}
                                    alt="preload"
                                    crossOrigin="anonymous"
                                />
                            ))}
                        </div>
                    ))
                }
            </div>
        </div>
    );
};

export default AnalysisReport;